<script setup lang="ts">
import { ref } from 'vue'

import { flickitySliderProps } from '../../composables/use-flickity-slider'
import { useParagraphTypeSlider } from '../../composables/use-paragraph-type-slider'
import { shouldPrioritizeInitialSliderTeaser } from '../../utils/image-priority'
import Icon from '../shared/Icon.vue'
import AiGeneratedBadge from '../shared/AiGeneratedBadge.vue'
import TeaserGroup from '../teaser/TeaserGroup.vue'
import ParagraphTypeEmbed from './ParagraphTypeEmbed.vue'
import SliderPicture from './ParagraphTypeSlider/Picture.vue'
import ParagraphTypeTmdbImage from './ParagraphTypeTmdbImage.vue'

import type { Brand } from '@xcel-next/core/enums'
import type { ComposedMetaData } from '@xcel-next/composer-schema'
import type { SliderParagraph, SliderGalleryItem } from '../../utils/paragraph-schema/paragraphs'

defineOptions({ name: 'ParagraphTypeSlider' })

const props = withDefaults(defineProps<{
  galleryItems?: SliderGalleryItem[],
  layout?: SliderParagraph['slider']['layout'],
  brand?: Brand,
  isPreview?: boolean,
  includeDepth?: number,
  metaData?: ComposedMetaData,
  singleSlideUrls?: boolean,
  sliderNo?: number,
  initialIndex?: number,
  listOrderPosition?: number | null
}>(), {
  isPreview: false,
  includeDepth: 0,
  singleSlideUrls: false,
  sliderNo: flickitySliderProps.sliderNo.default,
  initialIndex: flickitySliderProps.initialIndex.default
})

const flickityDiv = ref<HTMLElement | null>(null)

const {
  flickity,
  viewMode,
  viewportWidth,
  currentIndex,
  resolvedInitialIndex,
  sliderControlDescription,
  flickityContainerClasses,
  sliderContainerClasses,
  progressMarkerClasses,
  supportedEmbedsString,
  overviewEnabled,
  composedTeasers,
  isSupportedEmbed,
  isImagePortraitFormat,
  getImageExternalLinkAttributes,
  getFigureStyleClassByFormat,
  srcSetName,
  postFormatImageDescription,
  getLoadingValueByIndex,
  isSliderPlayerActive,
  paragraphsData,
  paragraphsMetaData,
  buttonPositionLogic,
  handleBottomBackToGallery,
  keepTabbingFocusInArticleSlider,
  moveToSlide
} = useParagraphTypeSlider({
  flickityDiv,
  galleryItems: () => props.galleryItems ?? [],
  brand: () => props.brand,
  isPreview: () => props.isPreview,
  metaData: () => props.metaData,
  singleSlideUrls: () => props.singleSlideUrls,
  sliderNo: () => props.sliderNo,
  initialIndex: () => props.initialIndex
})

// Exposed so services/the-gui's model-to-renderer-adapter/Slider.vue can move the slide on mouseover via $refs
defineExpose({ moveToSlide })

const prioritizeSliderTeaser = (index: number): boolean => shouldPrioritizeInitialSliderTeaser(
  props.listOrderPosition,
  props.includeDepth,
  index === resolvedInitialIndex.value,
  props.isPreview
)
</script>

<template>
  <div>
    <div v-if="viewMode === 'slider'"
         ref="flickityDiv"
         :class="flickityContainerClasses"
         :aria-label="`Karusell: ${galleryItems?.length ?? 0} Inhalte. ${sliderControlDescription}`"
         class="bx-slider bx-slider--paragraph-type-slider bx-typo--paragraph-type-slider"
         @keydown.enter="keepTabbingFocusInArticleSlider()">
      <button v-if="overviewEnabled"
              class="bx-slider__button-overview bx-slider__button-overview--top"
              @click="viewMode = 'overview'">
        Übersicht
        <Icon name="overview" />
      </button>
      <div class="bx-slider__progress">
        <div class="bx-slider__progress-bar">
          <div />
          <span :class="progressMarkerClasses" />
        </div>
        <div v-if="props.galleryItems"
             class="bx-slider__progress-count">
          ({{ currentIndex+1 }}/{{ galleryItems?.length ?? 0 }})
        </div>
      </div>
      <div v-for="(item, index) in galleryItems"
           :id="`bx-js-slider-cell-${sliderNo}-${index}`"
           :key="`slider-cell-${sliderNo}-${index}`"
           class="bx-slider__cell"
           :class="[{ 'bx-slider__cell--first-slide': !isPreview && index === resolvedInitialIndex, 'flickity-cell': !!flickity }]"
           :data-button-position-logic="buttonPositionLogic(item)"
           :data-cell-index="index">
        <span v-if="!isPreview && singleSlideUrls"
              :id="`image=${index + 1}`"
              class="bx-slider__deep-link-target"
              aria-hidden="true" />
        <figure v-if="item.paragraphType === 'image' && item.image"
                :class="getFigureStyleClassByFormat(isImagePortraitFormat(item))">
          <component :is="item.image.externalUrl ? 'a' : 'div'"
                     v-bind="getImageExternalLinkAttributes(item)">
            <div v-if="isImagePortraitFormat(item)"
                 class="bx-slider__blur-wrapper">
              <div :id="`bx-js-slider-cell-bg-${sliderNo}-${index}`"
                   class="bx-slider__blur-background" />
              <SliderPicture :item="item"
                             :active-slide="index === currentIndex"
                             :src-set-name="srcSetName(item)"
                             :brand="brand"
                             :viewport-width="viewportWidth"
                             :is-preview="isPreview" />
            </div>
            <SliderPicture v-else
                           :item="item"
                           :active-slide="index === currentIndex"
                           :src-set-name="srcSetName(item)"
                           :brand="brand"
                           :viewport-width="viewportWidth"
                           :is-preview="isPreview" />
            <AiGeneratedBadge v-if="item.image.isAiGenerated" />
            <figcaption>
              <span
                v-if="item.image.imageDescription && item.image.imageDescription.html && item.image.imageDescription.html.length > 0"
                class="bx-content__media-caption bx-content__media-img-description"
                v-html="postFormatImageDescription(item.image.imageDescription.html)" />
              <span v-if="item.image && item.image.imageCredit && item.image.imageCredit.length > 0"
                    class="bx-content__media-credit bx-content__media-img-credit">
                Foto: {{ item.image.imageCredit }}
              </span>
            </figcaption>
          </component>
        </figure>
        <ParagraphTypeEmbed v-else-if="item.paragraphType === 'embed' && item.embed && isSupportedEmbed(item.embed.embedType)"
                            :embed="item.embed"
                            :is-slider-element="true"
                            :is-preview="isPreview" />
        <ParagraphTypeTmdbImage v-else-if="item.paragraphType === 'tmdbImage' && item.tmdbImage"
                                :image="item.tmdbImage"
                                :loading="getLoadingValueByIndex(index)" />
        <div v-else-if="item.paragraphType === 'embed' && item.embed && (item.embed.embedType === undefined || item.embed.embedType.length === 0)"
             class="bx-slider__unavailable">
          <Icon name="supported-cloud" />
          the slider supports the following embeds:<br>{{ supportedEmbedsString }}
        </div>
        <div v-else-if="item.paragraphType === 'embed' && item.embed"
             class="bx-slider__unavailable">
          <Icon name="unavailable-cloud" />
          {{ item.embed.embedType }} embeds are not supported in the slider!
        </div>
        <!-- The paragraphs component expects an array so we wrap the item itself in an array since it has the same structure as paragraphs -->
        <paragraphs v-else
                    :paragraphs="paragraphsData(item)"
                    :meta-data="paragraphsMetaData(item)"
                    :data-is-slider-child="true"
                    :data-slider-initial-slide="index === resolvedInitialIndex"
                    :data-slider-player-active="isSliderPlayerActive(index)"
                    :prioritize-first-teaser-image="prioritizeSliderTeaser(index)"
                    :brand="brand"
                    :include-depth="includeDepth + 1" />
        <paragraphs v-if="item.paragraphs?.length ?? 0 > 0"
                    :paragraphs="item.paragraphs"
                    :meta-data="metaData"
                    :brand="brand"
                    :include-depth="includeDepth + 1" />
      </div>
    </div>
    <div v-else-if="viewMode === 'overview'"
         :class="sliderContainerClasses">
      <button class="bx-slider__button-overview bx-slider__button-overview--top"
              @click="viewMode = 'slider'">
        <Icon name="arrow_pager-prev" />
        Zurück zur Galerie oben
      </button>
      <template v-if="overviewEnabled">
        <TeaserGroup :teasers="composedTeasers"
                     teaser-group-type="bx-teaser-container--one-half-width"
                     :include-depth="6"
                     :list-order-position="6"
                     page-type="article"
                     :is-slider-overview="true"
                     :brand="brand" />
        <section class="bx-slider__button-wrapper">
          <button class="bx-slider__button-overview bx-slider__button-overview--bottom"
                  @click="handleBottomBackToGallery">
            <Icon name="arrow_pager-prev" />
            Zurück zur Galerie unten
          </button>
        </section>
      </template>
    </div>
  </div>
</template>
